/**
 * The relations menu (spec §8.15): every predicate linking blocks on this page, with counts, a
 * visibility toggle and a style; query tracks; and the style editor writing the workspace palette.
 * Styles are workspace-level (§7.5) and tracks page-level (§7.2), both Loro state.
 */
import type { LoroDoc } from "loro-crdt";
import { Spline, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePrefixes } from "../api/hooks";
import { mountSparqlEditor, type SparqlEditorHandle } from "../graph/SparqlEditor";
import type { DocHandle } from "../sync/docManager";
import { addTrack, createStyle, DEFAULT_STYLE, putStyle, removeTrack, setRailBlockLinks, setRailEnabled, summarisePredicates, updateTrack, type PageRelation, type RailStyle, type RailTrack } from "./model";
import { parseIri } from "../lib/iri";
import { Rich, useT } from "../i18n";
import { usePageRelations, usePredicateLabel, useQueryTracks, useRailConfig, useRailStyles, type QueryTrackState } from "./useRail";

const QUERY_TEMPLATE = `PREFIX km: <https://w3id.org/kss/ns#>
# rows: ?source and ?target are blocks of this page; ?label is optional
SELECT ?source ?target WHERE {
  ?source km:mentions ?target .
}`;

/** [M24] the workspace rail's template: pages that a page-link block on another page points at */
const WORKSPACE_QUERY_TEMPLATE = `PREFIX km: <https://w3id.org/kss/ns#>
# rows: ?source and ?target are pages of this workspace; ?label is optional
SELECT ?source ?target WHERE {
  ?block km:inDocument ?source ; km:references ?target .
}`;

export interface WorkspaceRailScope {
  /** the relations to list and count: workspace links between pages (§8.25) */
  relations: PageRelation[];
  /** whether a query row's endpoints are drawn (both pages are in the tree) */
  drawn(source: string, target: string): boolean;
}

export function RelationsMenu({ handle, wsDoc, workspaceId, readOnly, workspace }: { handle: DocHandle; wsDoc: LoroDoc | null; workspaceId: string; readOnly: boolean; workspace?: WorkspaceRailScope }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const config = useRailConfig(handle.doc);
  const styles = useRailStyles(wsDoc);
  // on the workspace home (§8.25) the handle is the workspace document and its relations come in
  const pageRelations = usePageRelations(handle).relations;
  const relations = workspace?.relations ?? pageRelations;
  const { states: queryStates, refresh: refreshQuery } = useQueryTracks(workspaceId, handle.crdtDocId, config, false);
  const predicates = useMemo(() => summarisePredicates(relations), [relations]);
  const [editingStyle, setEditingStyle] = useState<string | null>(null);
  const [queryDialog, setQueryDialog] = useState<RailTrack | "new" | null>(null);
  const canEditStyles = !readOnly && !!wsDoc;
  const offPage = predicates.reduce((n, p) => n + p.offPage, 0);

  const trackFor = (iri: string) => config.tracks.find((tr) => tr.source.kind === "predicate" && tr.source.iri === iri);
  const ensureStyle = (label: string): string => (wsDoc ? createStyle(wsDoc, label) : "default");
  const togglePredicate = (iri: string, label: string, on: boolean) => {
    const track = trackFor(iri);
    if (track) updateTrack(handle.doc, track.id, { visible: on });
    else if (on) addTrack(handle.doc, { kind: "predicate", iri }, ensureStyle(label));
    if (on && !config.enabled) setRailEnabled(handle.doc, true);
  };

  return (
    <div className="relative">
      <button className="flex items-center gap-1 rounded px-2 py-0.5 text-xs hover:bg-neutral-200 dark:hover:bg-neutral-800" onClick={() => setOpen((o) => !o)} data-testid="rail-menu-button" data-enabled={config.enabled}>
        <Spline size={14} /> {t("rail.button")}
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-40 w-[26rem] rounded-md border border-neutral-200 bg-[var(--surface)] p-3 text-xs shadow-[var(--shadow-lg)] dark:border-neutral-700" data-testid="rail-menu">
          <div className="mb-2 flex items-center gap-2">
            <label className="flex items-center gap-1 font-medium">
              <input type="checkbox" checked={config.enabled} disabled={readOnly} onChange={(e) => setRailEnabled(handle.doc, e.target.checked)} data-testid="rail-enabled" />
              {t("rail.show")}
            </label>
            <div className="grow" />
            <button onClick={() => setOpen(false)} aria-label={t("common.close")}>
              <X size={14} />
            </button>
          </div>

          <h4 className="mt-2 font-semibold uppercase text-neutral-500">{t("rail.onPage")}</h4>
          {predicates.length === 0 && <p className="text-neutral-500">{t("rail.none")}</p>}
          <ul className="space-y-1" data-testid="rail-predicates">
            {predicates.map((p) => (
              <PredicateRow
                key={p.iri}
                iri={p.iri}
                onPage={p.onPage}
                offPage={p.offPage}
                track={trackFor(p.iri)}
                styles={styles}
                readOnly={readOnly}
                canEditStyles={canEditStyles}
                onToggle={togglePredicate}
                onStyle={(track, styleId) => updateTrack(handle.doc, track.id, { styleId })}
                onNewStyle={(track, label) => updateTrack(handle.doc, track.id, { styleId: ensureStyle(label) })}
                onEditStyle={setEditingStyle}
              />
            ))}
          </ul>
          {offPage > 0 && (
            <p className="mt-1 text-neutral-500" data-testid="rail-offpage">
              {workspace ? t("rail.ws.offTree", { count: offPage }) : t("rail.offPage", { count: offPage })}
            </p>
          )}
          {workspace && (
            <label className="mt-2 flex items-center gap-1">
              <input type="checkbox" checked={!!config.blockLinks} disabled={readOnly} onChange={(e) => setRailBlockLinks(handle.doc, e.target.checked)} data-testid="rail-block-links" />
              {t("rail.ws.blockLinks")}
            </label>
          )}

          <h4 className="mt-3 font-semibold uppercase text-neutral-500">{t("rail.queryTracks")}</h4>
          <ul className="space-y-1" data-testid="rail-query-tracks">
            {config.tracks
              .filter((tr) => tr.source.kind === "query")
              .map((tr) => (
                <li key={tr.id} data-testid="rail-query-track">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={tr.visible} disabled={readOnly} onChange={(e) => updateTrack(handle.doc, tr.id, { visible: e.target.checked })} />
                    <span className="grow truncate">{tr.label || t("rail.query")}</span>
                  <StyleSelect track={tr} styles={styles} readOnly={readOnly} canEditStyles={canEditStyles} onStyle={(id) => updateTrack(handle.doc, tr.id, { styleId: id })} onNew={() => updateTrack(handle.doc, tr.id, { styleId: ensureStyle(tr.label || t("rail.query")) })} onEdit={setEditingStyle} />
                  {!readOnly && (
                    <>
                      <button className="underline" onClick={() => setQueryDialog(tr)}>{t("common.edit")}</button>
                      <button className="underline" onClick={() => removeTrack(handle.doc, tr.id)}>{t("common.remove")}</button>
                    </>
                  )}
                  </div>
                  {tr.visible && config.enabled && <QueryTrackStatus state={queryStates.get(tr.id)} drawn={workspace?.drawn ?? ((s, o) => parseIri(s)?.doc === handle.crdtDocId && parseIri(o)?.doc === handle.crdtDocId)} onRetry={() => refreshQuery(tr.id)} />}
                </li>
              ))}
          </ul>
          {!readOnly && (
            <button className="mt-1 underline" onClick={() => setQueryDialog("new")} data-testid="rail-add-query">
              {t("rail.addQuery")}
            </button>
          )}

          {editingStyle && wsDoc && (
            <StyleEditor
              styleId={editingStyle}
              style={styles[editingStyle] ?? DEFAULT_STYLE}
              readOnly={!canEditStyles}
              onChange={(s) => putStyle(wsDoc, editingStyle, s)}
              onClose={() => setEditingStyle(null)}
            />
          )}
        </div>
      )}
      {queryDialog && (
        <QueryTrackDialog
          template={workspace ? WORKSPACE_QUERY_TEMPLATE : QUERY_TEMPLATE}
          workspaceId={workspaceId}
          track={queryDialog === "new" ? null : queryDialog}
          onCancel={() => setQueryDialog(null)}
          onSave={(label, sparql) => {
            if (queryDialog === "new") {
              addTrack(handle.doc, { kind: "query", sparql }, ensureStyle(label || t("rail.query")), label || undefined);
              if (!config.enabled) setRailEnabled(handle.doc, true);
            } else updateTrack(handle.doc, queryDialog.id, { label: label || undefined, source: { kind: "query", sparql } });
            setQueryDialog(null);
          }}
        />
      )}
    </div>
  );
}

/** A query track's last run: its error (a failing query otherwise looks like an empty one), how many
 *  rows connect blocks of this page, and whether the result is a cached one replayed offline. */
function QueryTrackStatus({ state, drawn, onRetry }: { state: QueryTrackState | undefined; drawn(source: string, target: string): boolean; onRetry(): void }) {
  const t = useT();
  if (!state || state.loading) return <p className="ml-5 text-neutral-500" data-testid="rail-query-status" data-state="loading">{t("graph.running")}</p>;
  if (state.error && !state.stale) {
    return (
      <p className="ml-5 break-words text-red-700 dark:text-red-400" data-testid="rail-query-status" data-state="error" title={state.error}>
        {t("rail.queryFailed", { error: state.error.length > 200 ? `${state.error.slice(0, 200)}…` : state.error })}{" "}
        <button className="underline" onClick={onRetry}>{t("rail.retry")}</button>
      </p>
    );
  }
  const onPage = state.edges.filter((e) => drawn(e.source, e.target)).length;
  const elsewhere = state.edges.length - onPage;
  return (
    <p className="ml-5 text-neutral-500" data-testid="rail-query-status" data-state={state.stale ? "stale" : "ok"}>
      {t("rail.rowsOnPage", { count: onPage })}
      {elsewhere ? ` · ${t("rail.elsewhere", { count: elsewhere })}` : ""}
      {state.stale ? ` · ${t("rail.cached")}` : " "}
      {!state.stale && <button className="underline" onClick={onRetry}>{t("rail.rerun")}</button>}
    </p>
  );
}

function PredicateRow(props: {
  iri: string;
  onPage: number;
  offPage: number;
  track: RailTrack | undefined;
  styles: Record<string, RailStyle>;
  readOnly: boolean;
  canEditStyles: boolean;
  onToggle(iri: string, label: string, on: boolean): void;
  onStyle(track: RailTrack, styleId: string): void;
  onNewStyle(track: RailTrack, label: string): void;
  onEditStyle(styleId: string): void;
}) {
  const t = useT();
  const { iri, onPage, offPage, track } = props;
  const label = usePredicateLabel(iri);
  return (
    <li className="flex items-center gap-2" data-testid="rail-predicate" data-iri={iri}>
      <input type="checkbox" checked={!!track?.visible} disabled={props.readOnly || onPage === 0} onChange={(e) => props.onToggle(iri, label, e.target.checked)} data-testid="rail-predicate-toggle" aria-label={t("rail.showPredicate", { label })} />
      <span className="grow truncate" title={iri}>
        {label}
      </span>
      <span className="text-neutral-500" data-testid="rail-predicate-count">
        {onPage}
        {offPage ? ` · ${t("rail.offPageCount", { count: offPage })}` : ""}
      </span>
      {track && <StyleSelect track={track} styles={props.styles} readOnly={props.readOnly} canEditStyles={props.canEditStyles} onStyle={(id) => props.onStyle(track, id)} onNew={() => props.onNewStyle(track, label)} onEdit={props.onEditStyle} />}
    </li>
  );
}

function StyleSelect({ track, styles, readOnly, canEditStyles, onStyle, onNew, onEdit }: { track: RailTrack; styles: Record<string, RailStyle>; readOnly: boolean; canEditStyles: boolean; onStyle(id: string): void; onNew(): void; onEdit(id: string): void }) {
  const t = useT();
  const current = styles[track.styleId] ?? DEFAULT_STYLE;
  return (
    <span className="flex items-center gap-1">
      <button className="h-3 w-6 rounded-sm border border-neutral-300" style={{ background: current.stroke }} title={t("rail.editStyle")} onClick={() => onEdit(track.styleId)} data-testid="rail-style-swatch" />
      <select
        className="max-w-28 rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700"
        value={styles[track.styleId] ? track.styleId : ""}
        disabled={readOnly}
        data-testid="rail-style-select"
        onChange={(e) => (e.target.value === "__new__" ? onNew() : onStyle(e.target.value))}
      >
        {!styles[track.styleId] && <option value="">{t("rail.defaultStyle")}</option>}
        {Object.entries(styles).map(([id, s]) => (
          <option key={id} value={id}>
            {s.label || id.slice(0, 6)}
          </option>
        ))}
        {canEditStyles && <option value="__new__">{t("rail.newStyle")}</option>}
      </select>
    </span>
  );
}

function StyleEditor({ styleId, style, readOnly, onChange, onClose }: { styleId: string; style: RailStyle; readOnly: boolean; onChange(s: RailStyle): void; onClose(): void }) {
  const t = useT();
  const set = <K extends keyof RailStyle>(k: K, v: RailStyle[K]) => onChange({ ...style, [k]: v });
  return (
    <div className="mt-3 space-y-1 rounded border border-neutral-200 p-2 dark:border-neutral-700" data-testid="rail-style-editor" data-style-id={styleId}>
      <div className="flex items-center gap-2 font-semibold">
        {t("rail.style")} <span className="font-normal text-neutral-500">{t("rail.styleScope")}</span>
        <div className="grow" />
        <button onClick={onClose} aria-label={t("rail.closeStyle")}>
          <X size={12} />
        </button>
      </div>
      <label className="flex items-center gap-2">
        {t("settings.name")}
        <input className="grow rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={style.label} disabled={readOnly} onChange={(e) => set("label", e.target.value)} data-testid="rail-style-label" />
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1">
          {t("rail.colour")}
          <input type="color" value={style.stroke} disabled={readOnly} onChange={(e) => set("stroke", e.target.value)} data-testid="rail-style-stroke" />
        </label>
        <label className="flex items-center gap-1">
          {t("rail.width")}
          <input type="number" min={1} max={8} step={0.5} className="w-14 rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={style.width} disabled={readOnly} onChange={(e) => set("width", Math.min(8, Math.max(1, Number(e.target.value) || 1)))} data-testid="rail-style-width" />
        </label>
        <label className="flex items-center gap-1">
          {t("rail.line")}
          <select className="rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={style.dash} disabled={readOnly} onChange={(e) => set("dash", e.target.value as RailStyle["dash"])} data-testid="rail-style-dash">
            <option value="solid">{t("rail.dash.solid")}</option>
            <option value="dashed">{t("rail.dash.dashed")}</option>
            <option value="dotted">{t("rail.dash.dotted")}</option>
            <option value="dashdot">{t("rail.dash.dashdot")}</option>
          </select>
        </label>
        <label className="flex items-center gap-1">
          {t("rail.side")}
          <select className="rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={style.side} disabled={readOnly} onChange={(e) => set("side", e.target.value as RailStyle["side"])} data-testid="rail-style-side">
            <option value="left">{t("rail.side.left")}</option>
            <option value="right">{t("rail.side.right")}</option>
          </select>
        </label>
        <label className="flex items-center gap-1">
          {t("rail.arrow")}
          <select className="rounded border border-neutral-300 bg-transparent px-1 dark:border-neutral-700" value={style.arrow} disabled={readOnly} onChange={(e) => set("arrow", e.target.value as RailStyle["arrow"])} data-testid="rail-style-arrow">
            <option value="none">{t("rail.arrow.none")}</option>
            <option value="target">{t("rail.arrow.target")}</option>
            <option value="both">{t("rail.arrow.both")}</option>
          </select>
        </label>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={style.showLabel} disabled={readOnly} onChange={(e) => set("showLabel", e.target.checked)} data-testid="rail-style-show-label" />
          {t("rail.showLabel")}
        </label>
      </div>
    </div>
  );
}

function QueryTrackDialog({ workspaceId, track, template, onCancel, onSave }: { workspaceId: string; track: RailTrack | null; template: string; onCancel(): void; onSave(label: string, sparql: string): void }) {
  const t = useT();
  const [label, setLabel] = useState(track?.label ?? "");
  const host = useRef<HTMLDivElement>(null);
  const editor = useRef<SparqlEditorHandle | null>(null);
  const prefixes = usePrefixes();
  const prefixesRef = useRef<Record<string, string>>({});
  const save = useRef<(q: string) => void>(() => {});
  const initial = track?.source.kind === "query" ? track.source.sparql : template;
  useEffect(() => {
    prefixesRef.current = prefixes.data ?? {};
    save.current = (q: string) => onSave(label, q);
  });
  useEffect(() => {
    if (!host.current) return;
    const h = mountSparqlEditor(host.current, { initial, workspaceId, prefixes: () => prefixesRef.current, onRun: (q) => save.current(q) });
    editor.current = h;
    return () => h.destroy();
    // the editor is mounted once per dialog (§8 rule 6: imperative editor surfaces)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(28,27,26,0.36)] p-8" onClick={onCancel}>
      <div className="w-[44rem] rounded border border-neutral-300 bg-white p-4 text-sm shadow-xl dark:border-neutral-700 dark:bg-neutral-900" onClick={(e) => e.stopPropagation()} data-testid="rail-query-dialog">
        <h2 className="mb-2 font-semibold">{track ? t("rail.dialog.edit") : t("rail.dialog.add")}</h2>
        <p className="mb-2 text-xs text-neutral-500">
          <Rich text={t("rail.dialog.intro")} strong="font-mono" />
        </p>
        <input className="mb-2 w-full rounded border border-neutral-300 bg-transparent px-2 py-1 dark:border-neutral-700" placeholder={t("common.label")} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="rail-query-label" />
        <div ref={host} className="max-h-72 overflow-auto rounded border border-neutral-200 dark:border-neutral-800" data-testid="rail-query-editor" />
        <div className="mt-3 flex justify-end gap-2">
          <button className="underline" onClick={onCancel}>{t("common.cancel")}</button>
          <button className="rounded bg-accent px-3 py-1 text-white" onClick={() => onSave(label, editor.current?.value() ?? initial)} data-testid="rail-query-save">
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
